'use client';

import {
  createContext,
  useContext,
  useReducer,
  useState,
  useCallback,
  useRef,
  useEffect,
  ReactNode,
} from 'react';
import { Project, Chat, Message, Agent } from '@/types';
import { chatsApi, ChatResponse, streamMessage, deleteMessage as apiDeleteMessage } from '@/api/chats';
import { projectsApi, ProjectResponse } from '@/api/projects';
import { trackCreateProject, trackStartChat, trackSendMessage } from '@/lib/analytics';
import { useAuth } from './AuthContext';
import { useWorkspace } from './WorkspaceContext';

// Types
/** Agent id sent to the backend (Agno); no agents table in DB anymore. */
const DEFAULT_AGENT_ID = 'default';

/** Display object for UI (agent name in header, empty state, etc.). */
const DEFAULT_AGENT_DISPLAY: Agent = {
  id: DEFAULT_AGENT_ID,
  name: 'Paladino',
  prompt: '',
  subtitle: '',
  integrations: [],
  tools: [],
  createdAt: new Date(0),
};

interface ChatState {
  projects: Project[];
  standaloneChats: Chat[];
  selectedProjectId: string | null;
  selectedChatId: string | null;
  messages: Message[];
  isStreaming: boolean;
  isTypingAnimation: boolean;
  typingMessageId: string | null;
  isLoading: boolean;
}

type ChatAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_PROJECTS'; payload: Project[] }
  | { type: 'SET_STANDALONE_CHATS'; payload: Chat[] }
  | { type: 'ADD_PROJECT'; payload: Project }
  | { type: 'SET_SELECTED_PROJECT'; payload: string | null }
  | { type: 'CREATE_CHAT'; payload: { projectId: string; chat: Chat } }
  | { type: 'SELECT_CHAT'; payload: { chatId: string; projectId?: string; messages: Message[] } }
  | { type: 'CLEAR_CHAT' }
  | { type: 'ADD_MESSAGE'; payload: Message }
  | { type: 'UPDATE_LAST_MESSAGE'; payload: string }
  | { type: 'SET_MESSAGES'; payload: Message[] }
  | { type: 'SET_IS_STREAMING'; payload: boolean }
  | { type: 'ADD_TOOL_USAGE'; payload: { messageId?: string; tool: any } }
  | { type: 'UPDATE_TOOL_STATE'; payload: { toolCallId: string; state: string; result?: any; approvalMeta?: { approvedBy?: string; approvedAt?: string } } }
  | { type: 'UPDATE_MESSAGE_METADATA'; payload: { messageId?: string; metadata: any } }
  | { type: 'START_TYPING_ANIMATION'; payload: { messageId: string } }
  | { type: 'STOP_TYPING_ANIMATION'; payload: { messageId: string } }
  | { type: 'UPDATE_PROJECT_CHAT_MESSAGES'; payload: { projectId: string; chatId: string; messages: Message[] } }
  | { type: 'ADD_CHAT_TO_PROJECT'; payload: { projectId: string; chat: Chat } }
  | { type: 'ADD_STANDALONE_CHAT'; payload: Chat }
  | { type: 'DELETE_MESSAGE'; payload: { messageId: string } }
  | { type: 'RESET_STATE' };

interface ChatContextValue extends ChatState {
  // Computed - Workspace ID from WorkspaceContext
  workspaceId: string | null;

  // UI - Preview/Analysis side panel
  sidePanelOpen: boolean;
  sidePanelTab: 'preview' | 'ide';
  previewUrl: string | null;
  openSidePanel: (tab: 'preview' | 'ide') => void;
  closeSidePanel: () => void;
  setPreviewUrl: (url: string | null) => void;

  // UI - Chat mode (prompt mode)
  chatMode: 'default' | 'plan' | 'security_review';
  setChatMode: (mode: 'default' | 'plan' | 'security_review') => void;

  // Actions - Projects
  addProject: (name: string, description: string) => Promise<void>;
  setSelectedProject: (projectId: string | null) => void;
  loadProjects: () => Promise<void>;

  // Actions - Chats
  createChat: (projectId: string, title: string) => Promise<string>;
  selectChat: (chatId: string, projectId?: string) => Promise<void>;
  clearChat: () => void;

  // Actions - Messages
  addMessage: (message: Omit<Message, 'id' | 'timestamp'>) => void;
  sendMessage: (
    content: string,
    options?: {
      dependencies?: Record<string, unknown>;
      metadata?: Record<string, unknown>;
      outputSchema?: Record<string, unknown>;
      files?: File[];
      uiAttachments?: Array<{
        name: string;
        type?: string;
        size?: number;
        previewUrl?: string;
      }>;
    }
  ) => Promise<string>;
  updateLastMessage: (content: string) => void;
  updateMessageMetadata: (metadata: any, messageId?: string) => void;
  addToolUsage: (tool: any, messageId?: string) => void;
  updateToolState: (toolCallId: string, newState: string, result?: any, approvalMeta?: { approvedBy?: string; approvedAt?: string }) => void;
  deleteMessageById: (messageId: string) => Promise<void>;
  regenerateMessage: (messageId: string) => Promise<void>;
  setMessages: (messages: Message[]) => void;
  setIsStreaming: (isStreaming: boolean) => void;
  startTypingAnimation: (messageId: string) => void;
  stopTypingAnimation: (messageId: string) => void;

  // Actions - Refs
  registerChatInput: (el: HTMLElement | null) => void;
  focusChatInput: () => void;

  // Quick action: fill input with suggested message (e.g. from empty state buttons)
  setSuggestedMessage: (message: string | null) => void;
  suggestedMessage: string | null;

  // Helpers
  getCurrentProject: () => Project | undefined;
  getCurrentChat: () => Chat | undefined;
  getCurrentAgent: () => Agent;
}

// Helper function to convert API chat to app chat
function convertChatResponse(chatResponse: ChatResponse): Chat {
  return {
    id: chatResponse.id,
    title: chatResponse.title || 'Nova conversa',
    projectId: chatResponse.projectId || undefined,
    messages: [],
    createdAt: new Date(chatResponse.createdAt),
  };
}

function convertProjectResponse(
  p: ProjectResponse,
  chats: Chat[] = [],
): Project {
  return {
    id: p.id,
    name: p.name,
    description: p.description || undefined,
    agentId: DEFAULT_AGENT_ID,
    chats,
  };
}

function convertChatMessageToMessage(msg: {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt: string;
  metadata?: any;
}): Message {
  return {
    id: msg.id,
    role: msg.role === 'system' ? 'assistant' : msg.role,
    content: msg.content,
    timestamp: new Date(msg.createdAt),
    metadata: msg.metadata,
  };
}

// Initial state
const initialState: ChatState = {
  projects: [],
  standaloneChats: [],
  selectedProjectId: null,
  selectedChatId: null,
  messages: [],
  isStreaming: false,
  isTypingAnimation: false,
  typingMessageId: null,
  isLoading: true,
};

function extractFirstUrl(input: string): string | null {
  const m = input.match(/https?:\/\/[^\s"')\]]+/i);
  return m?.[0] ?? null;
}

function extractPreviewUrlFromToolResult(result: unknown): string | null {
  if (typeof result === 'string') {
    return extractFirstUrl(result);
  }
  if (!result || typeof result !== 'object') {
    return null;
  }

  const seen = new Set<unknown>();
  const stack: unknown[] = [result];

  const keyHints = new Set([
    'previewUrl',
    'preview_url',
    'previewURL',
    'url',
    'preview',
    'previewLink',
    'preview_link',
  ]);

  while (stack.length > 0) {
    const cur = stack.pop();
    if (!cur || typeof cur !== 'object') continue;
    if (seen.has(cur)) continue;
    seen.add(cur);

    if (Array.isArray(cur)) {
      for (const v of cur) stack.push(v);
      continue;
    }

    for (const [k, v] of Object.entries(cur as Record<string, unknown>)) {
      if (typeof v === 'string') {
        if (keyHints.has(k)) {
          const found = extractFirstUrl(v);
          if (found) return found;
        }
        const found = extractFirstUrl(v);
        if (found && /preview|localhost|vercel|netlify|railway|render/i.test(found)) return found;
      } else if (v && typeof v === 'object') {
        stack.push(v);
      }
    }
  }

  return null;
}

// Reducer
function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };

    case 'SET_PROJECTS':
      return {
        ...state,
        projects: action.payload,
      };

    case 'SET_STANDALONE_CHATS':
      return {
        ...state,
        standaloneChats: action.payload,
      };

    case 'ADD_PROJECT':
      return {
        ...state,
        projects: [...state.projects, action.payload],
        selectedProjectId: action.payload.id,
      };

    case 'SET_SELECTED_PROJECT':
      return {
        ...state,
        selectedProjectId: action.payload,
        selectedChatId: null,
        messages: [],
      };

    case 'CREATE_CHAT':
      return {
        ...state,
        projects: state.projects.map((p) =>
          p.id === action.payload.projectId
            ? { ...p, chats: [...p.chats, action.payload.chat] }
            : p
        ),
        selectedChatId: action.payload.chat.id,
        selectedProjectId: action.payload.projectId,
        messages: [],
      };

    case 'ADD_CHAT_TO_PROJECT':
      return {
        ...state,
        projects: state.projects.map((p) =>
          p.id === action.payload.projectId
            ? { ...p, chats: [...p.chats, action.payload.chat] }
            : p
        ),
        // Remove chat from standaloneChats if it has a projectId
        standaloneChats: state.standaloneChats.filter((c) => c.id !== action.payload.chat.id),
      };

    case 'ADD_STANDALONE_CHAT':
      // Only add if chat doesn't have a projectId and isn't already in the list
      if (action.payload.projectId) {
        // Chat has projectId, shouldn't be in standaloneChats
        return state;
      }
      const isAlreadyStandalone = state.standaloneChats.some((c) => c.id === action.payload.id);
      if (isAlreadyStandalone) {
        return state;
      }
      return {
        ...state,
        standaloneChats: [...state.standaloneChats, action.payload],
      };

    case 'SELECT_CHAT':
      return {
        ...state,
        selectedChatId: action.payload.chatId,
        selectedProjectId: action.payload.projectId || null,
        messages: action.payload.messages.map((m) => ({ ...m, isNew: false })),
        isTypingAnimation: false,
        typingMessageId: null,
      };

    case 'CLEAR_CHAT':
      return {
        ...state,
        messages: [],
        selectedChatId: null,
        isTypingAnimation: false,
        typingMessageId: null,
      };

    case 'ADD_MESSAGE':
      return {
        ...state,
        messages: [...state.messages, action.payload],
      };

    case 'UPDATE_LAST_MESSAGE':
      if (state.messages.length === 0) return state;
      const newMessages = [...state.messages];
      newMessages[newMessages.length - 1] = {
        ...newMessages[newMessages.length - 1],
        content: action.payload,
        loadingLabel: undefined,
      };
      return {
        ...state,
        messages: newMessages,
      };

    case 'UPDATE_MESSAGE_METADATA': {
      if (state.messages.length === 0) return state;
      const { messageId, metadata } = action.payload;
      const targetIndex = messageId
        ? state.messages.findIndex(m => m.id === messageId)
        : state.messages.length - 1;

      if (targetIndex === -1) return state;

      const updatedMessages = [...state.messages];
      const targetMsg = updatedMessages[targetIndex];

      // Deep merge metadata if needed, but for now simple merge
      const newMetadata = { ...targetMsg.metadata, ...metadata };
      // Special handling for tool_usage array merging if needed?
      // For now, assume the caller handles the array logic or we replace it.
      // But actually, we might want to append tool usage. 
      // Let's rely on the caller passing the correct complete metadata or specific fields.

      updatedMessages[targetIndex] = {
        ...targetMsg,
        metadata: newMetadata,
      };

      return {
        ...state,
        messages: updatedMessages,
      };
    }

    case 'ADD_TOOL_USAGE': {
      if (state.messages.length === 0) return state;
      const { messageId, tool } = action.payload;
      const targetIndex = messageId
        ? state.messages.findIndex(m => m.id === messageId)
        : state.messages.length - 1;

      if (targetIndex === -1) return state;

      const updatedMessages = [...state.messages];
      const targetMsg = updatedMessages[targetIndex];
      const currentTools = targetMsg.metadata?.tool_usage || [];

      // Check if tool already exists (by call ID) to avoid duplicates
      const exists = tool.toolCallId && currentTools.some((t: any) => t.toolCallId === tool.toolCallId);
      if (exists) return state;

      const newMetadata = {
        ...targetMsg.metadata,
        tool_usage: [...currentTools, tool]
      };

      updatedMessages[targetIndex] = {
        ...targetMsg,
        metadata: newMetadata,
      };

      return {
        ...state,
        messages: updatedMessages,
      };
    }

    case 'UPDATE_TOOL_STATE': {
      // Update the state of a specific tool by toolCallId across all messages.
      const { toolCallId, state: newToolState, result, approvalMeta } = action.payload;
      if (!toolCallId) return state;

      let found = false;
      const updatedMessages = state.messages.map((msg) => {
        const tools = msg.metadata?.tool_usage;
        if (!tools || !Array.isArray(tools)) return msg;

        const updatedTools = tools.map((t: any) => {
          if (t.toolCallId === toolCallId) {
            found = true;
            return {
              ...t,
              state: newToolState,
              ...(result !== undefined ? { result } : {}),
              ...(approvalMeta?.approvedBy ? { approvedBy: approvalMeta.approvedBy } : {}),
              ...(approvalMeta?.approvedAt ? { approvedAt: approvalMeta.approvedAt } : {}),
            };
          }
          return t;
        });

        if (!found) return msg;
        return {
          ...msg,
          metadata: { ...msg.metadata, tool_usage: updatedTools },
        };
      });

      return found ? { ...state, messages: updatedMessages } : state;
    }

    case 'DELETE_MESSAGE': {
      return {
        ...state,
        messages: state.messages.filter((m) => m.id !== action.payload.messageId),
      };
    }

    case 'SET_MESSAGES':
      return {
        ...state,
        messages: action.payload.map((m) => ({ ...m, isNew: false })),
        isTypingAnimation: false,
        typingMessageId: null,
      };

    case 'SET_IS_STREAMING':
      return {
        ...state,
        isStreaming: action.payload,
      };

    case 'START_TYPING_ANIMATION':
      return {
        ...state,
        isTypingAnimation: true,
        typingMessageId: action.payload.messageId,
      };

    case 'STOP_TYPING_ANIMATION':
      if (state.typingMessageId !== action.payload.messageId) return state;
      return {
        ...state,
        isTypingAnimation: false,
        typingMessageId: null,
      };

    case 'UPDATE_PROJECT_CHAT_MESSAGES':
      return {
        ...state,
        projects: state.projects.map((p) =>
          p.id === action.payload.projectId
            ? {
              ...p,
              chats: p.chats.map((c) =>
                c.id === action.payload.chatId
                  ? {
                    ...c,
                    messages: action.payload.messages.map((m) => ({
                      ...m,
                      isNew: false,
                    })),
                  }
                  : c
              ),
            }
            : p
        ),
      };

    case 'RESET_STATE':
      return {
        projects: [],
        standaloneChats: [],
        selectedProjectId: null,
        selectedChatId: null,
        messages: [],
        isStreaming: false,
        isTypingAnimation: false,
        typingMessageId: null,
        isLoading: false,
      };

    default:
      return state;
  }
}

// Context
const ChatContext = createContext<ChatContextValue | null>(null);

// Provider
export function ChatProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(chatReducer, initialState);
  const { user, isAuthenticated, refreshCredits } = useAuth();
  const { selectedWorkspaceId } = useWorkspace();
  const chatInputRef = useRef<HTMLElement | null>(null);
  const [suggestedMessage, setSuggestedMessageState] = useState<string | null>(null);
  const setSuggestedMessage = useCallback((message: string | null) => {
    setSuggestedMessageState(message);
  }, []);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [sidePanelTab, setSidePanelTab] = useState<'preview' | 'ide'>('preview');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [chatMode, setChatMode] = useState<'default' | 'plan' | 'security_review'>('default');

  const openSidePanel = useCallback((tab: 'preview' | 'ide') => {
    setSidePanelTab(tab);
    setSidePanelOpen(true);
  }, []);

  const closeSidePanel = useCallback(() => {
    setSidePanelOpen(false);
  }, []);

  const updateMessageMetadata = useCallback((metadata: any, messageId?: string) => {
    dispatch({ type: 'UPDATE_MESSAGE_METADATA', payload: { metadata, messageId } });
  }, []);
  const addToolUsage = useCallback((tool: any, messageId?: string) => {
    dispatch({ type: 'ADD_TOOL_USAGE', payload: { tool, messageId } });
  }, []);
  const updateToolState = useCallback((toolCallId: string, newState: string, result?: any, approvalMeta?: { approvedBy?: string; approvedAt?: string }) => {
    dispatch({ type: 'UPDATE_TOOL_STATE', payload: { toolCallId, state: newState, result, approvalMeta } });
  }, []);
  const setIsStreaming = useCallback((isStreaming: boolean) => {
    dispatch({ type: 'SET_IS_STREAMING', payload: isStreaming });
  }, []);
  const startTypingAnimation = useCallback((messageId: string) => {
    dispatch({ type: 'START_TYPING_ANIMATION', payload: { messageId } });
  }, []);
  const stopTypingAnimation = useCallback((messageId: string) => {
    dispatch({ type: 'STOP_TYPING_ANIMATION', payload: { messageId } });
  }, []);

  // Load projects and chats for current workspace
  const loadProjects = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      // Agents are loaded separately (everyone has access)
      // No need to reload agents here as they're loaded independently

      if (!selectedWorkspaceId) {
        dispatch({ type: 'SET_PROJECTS', payload: [] });
        dispatch({ type: 'SET_STANDALONE_CHATS', payload: [] });
        return;
      }

      // Load projects and chats for workspace
      const [projectsResponse, chatsResponse] = await Promise.all([
        projectsApi.listByWorkspace(selectedWorkspaceId),
        chatsApi.getAll(selectedWorkspaceId),
      ]);

      const chats = chatsResponse.map(convertChatResponse);

      // Separate standalone chats (without projectId) from project chats
      const standaloneChats: Chat[] = [];
      const chatsByProject: Record<string, Chat[]> = {};

      chats.forEach((chat) => {
        if (chat.projectId) {
          // Chat has a project
          if (!chatsByProject[chat.projectId]) {
            chatsByProject[chat.projectId] = [];
          }
          chatsByProject[chat.projectId].push(chat);
        } else {
          // Chat without project
          standaloneChats.push(chat);
        }
      });

      // Create app projects from API projects
      const projects: Project[] = projectsResponse.map((p) =>
        convertProjectResponse(p, chatsByProject[p.id] || []),
      );

      dispatch({ type: 'SET_PROJECTS', payload: projects });
      dispatch({ type: 'SET_STANDALONE_CHATS', payload: standaloneChats });

      // Ensure no chat is selected after loading (fresh start)
      dispatch({ type: 'SET_SELECTED_PROJECT', payload: null });
      dispatch({ type: 'CLEAR_CHAT' });
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [selectedWorkspaceId]);

  // Reset state when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      dispatch({ type: 'RESET_STATE' });
      setPreviewUrl(null);
      setSidePanelOpen(false);
      setSidePanelTab('preview');
      setChatMode('default');
    }
  }, [isAuthenticated]);

  // Track user ID and workspace ID to avoid reloading when only credits change
  const userIdRef = useRef<string | null>(null);
  const workspaceIdRef = useRef<string | null>(null);

  // Clear chat immediately when workspace changes
  useEffect(() => {
    const currentWorkspaceId = selectedWorkspaceId;
    const workspaceIdChanged = workspaceIdRef.current !== null && workspaceIdRef.current !== currentWorkspaceId;

    if (workspaceIdChanged) {
      // Clear chat immediately when workspace changes
      dispatch({ type: 'CLEAR_CHAT' });
      dispatch({ type: 'SET_SELECTED_PROJECT', payload: null });
      setPreviewUrl(null);
      setSidePanelOpen(false);
      setSidePanelTab('preview');
      setChatMode('default');
    }
  }, [selectedWorkspaceId]);

  // Load projects when workspace changes or user changes (but not when only credits change)
  useEffect(() => {
    const currentUserId = user?.id || null;
    const currentWorkspaceId = selectedWorkspaceId;
    const userIdChanged = userIdRef.current !== currentUserId;
    const workspaceIdChanged = workspaceIdRef.current !== currentWorkspaceId;

    if (userIdChanged) {
      userIdRef.current = currentUserId;
    }
    if (workspaceIdChanged) {
      workspaceIdRef.current = currentWorkspaceId;
    }

    if (isAuthenticated && user && selectedWorkspaceId) {
      // Reload if user ID changed OR workspace ID changed
      if (userIdChanged || workspaceIdChanged) {
        loadProjects();
      }
    } else if (isAuthenticated && user && !selectedWorkspaceId) {
      // Clear projects when no workspace is selected
      dispatch({ type: 'SET_PROJECTS', payload: [] });
      dispatch({ type: 'SET_STANDALONE_CHATS', payload: [] });
      workspaceIdRef.current = null;
    }
  }, [isAuthenticated, user?.id, selectedWorkspaceId, loadProjects]);

  // Actions - Projects
  const addProject = useCallback(
    async (name: string, description: string) => {
      try {
        if (!selectedWorkspaceId) {
          await loadProjects();
        }
        if (!selectedWorkspaceId) return;

        const created = await projectsApi.createInWorkspace(selectedWorkspaceId, {
          name,
          ...(description ? { description } : {}),
        });

        const newProject = convertProjectResponse(created, []);
        dispatch({ type: 'ADD_PROJECT', payload: newProject });
        trackCreateProject();
      } catch (error) {
        console.error('Error creating project:', error);
        await loadProjects();
        throw error;
      }
    },
    [selectedWorkspaceId, loadProjects],
  );

  const setSelectedProject = useCallback((projectId: string | null) => {
    dispatch({ type: 'SET_SELECTED_PROJECT', payload: projectId });
  }, []);

  // Actions - Chats
  const createChat = useCallback(
    async (projectId: string, title: string): Promise<string> => {
      try {
        const project = state.projects.find((p) => p.id === projectId);
        if (!project) {
          throw new Error('Project not found');
        }

        const effectiveWorkspaceId = selectedWorkspaceId || user?.currentWorkspaceId || undefined;
        const chatResponse = await chatsApi.create({
          ...(effectiveWorkspaceId ? { workspaceId: effectiveWorkspaceId } : {}),
          projectId,
          title: title || 'Nova conversa',
        });

        const newChat = convertChatResponse(chatResponse);
        dispatch({ type: 'CREATE_CHAT', payload: { projectId, chat: newChat } });
        trackStartChat(projectId);

        return newChat.id;
      } catch (error) {
        console.error('Error creating chat:', error);
        throw error;
      }
    },
    [state.projects, selectedWorkspaceId, user?.currentWorkspaceId],
  );

  const selectChat = useCallback(async (chatId: string, projectId?: string) => {
    if (chatId.startsWith('mockchat-')) {
      if (state.selectedChatId === chatId && state.messages.length > 0) return;
    }

    try {
      const chatResponse = await chatsApi.getById(chatId);
      const chat = convertChatResponse(chatResponse);
      const messagesResponse = await chatsApi.getMessages(chatId);
      const messages = messagesResponse.map(convertChatMessageToMessage);

      // Use projectId from chat if not provided (important for spaces chats)
      const effectiveProjectId = projectId || chat.projectId;

      // Update project chats if needed
      if (effectiveProjectId) {
        let project = state.projects.find((p) => p.id === effectiveProjectId);
        if (!project) {
          // Project doesn't exist in local state - reload projects to get it
          console.warn(`Project ${effectiveProjectId} not found in local state, reloading projects...`);
          await loadProjects();
          // After reload, get the updated state - we need to check if project exists now
          // Note: We can't access updated state here, so we'll just proceed with selection
          // The chat will be associated correctly when loadProjects completes
        } else {
          // Project exists - add chat if not already there
          if (!project.chats.find((c) => c.id === chatId)) {
            dispatch({ type: 'ADD_CHAT_TO_PROJECT', payload: { projectId: effectiveProjectId, chat } });
          }
        }
      } else {
        // It's a standalone chat - only add if not already in standaloneChats
        const existingChat = state.standaloneChats.find((c) => c.id === chatId);
        if (!existingChat) {
          // Also check if it's not in any project (shouldn't happen, but safety check)
          const isInProject = state.projects.some((p) => p.chats.some((c) => c.id === chatId));
          if (!isInProject) {
            dispatch({ type: 'ADD_STANDALONE_CHAT', payload: chat });
          }
        }
      }

      dispatch({
        type: 'SELECT_CHAT',
        payload: { chatId, projectId: effectiveProjectId, messages },
      });
    } catch (error) {
      console.error('Error selecting chat:', error);
      // Fallback to local state
      let chat: Chat | undefined;
      let effectiveProjectId: string | undefined = projectId;

      // Try to find chat in projects first
      for (const project of state.projects) {
        const foundChat = project.chats.find((c) => c.id === chatId);
        if (foundChat) {
          chat = foundChat;
          effectiveProjectId = effectiveProjectId || project.id;
          break;
        }
      }
      // If not found in projects, try standalone chats
      if (!chat) {
        chat = state.standaloneChats.find((c) => c.id === chatId);
        effectiveProjectId = effectiveProjectId || chat?.projectId;
      }

      dispatch({
        type: 'SELECT_CHAT',
        payload: { chatId, projectId: effectiveProjectId, messages: chat?.messages || [] },
      });
    }
  }, [state.projects, state.standaloneChats, loadProjects]);

  const clearChat = useCallback(() => {
    dispatch({ type: 'CLEAR_CHAT' });
    setPreviewUrl(null);
    setSidePanelOpen(false);
    setSidePanelTab('preview');
  }, []);

  // Actions - Messages
  const addMessage = useCallback(
    (message: Omit<Message, 'id' | 'timestamp'>) => {
      const newMessage: Message = {
        ...message,
        id: `msg-${Date.now()}-${Math.random()}`,
        timestamp: new Date(),
        isNew: true,
      };
      dispatch({ type: 'ADD_MESSAGE', payload: newMessage });
    }, []);

  const updateLastMessage = useCallback((content: string) => {
    dispatch({ type: 'UPDATE_LAST_MESSAGE', payload: content });
  }, []);

  const sendMessage = useCallback(async (
    content: string,
    options?: {
      dependencies?: Record<string, unknown>;
      metadata?: Record<string, unknown>;
      outputSchema?: Record<string, unknown>;
      files?: File[];
      uiAttachments?: Array<{
        name: string;
        type?: string;
        size?: number;
        previewUrl?: string;
      }>;
    }
  ): Promise<string> => {
    try {
      setIsStreaming(true);

      // Create chat if none selected (workspace comes from JWT on backend)
      let chatId = state.selectedChatId;
      if (!chatId) {
        const title = content.slice(0, 50) + (content.length > 50 ? '...' : '');
        const projectId: string | null = state.selectedProjectId;
        const effectiveProjectId: string | undefined = projectId || undefined;

        if (effectiveProjectId) {
          chatId = await createChat(effectiveProjectId, title);
          dispatch({
            type: 'SELECT_CHAT',
            payload: { chatId, projectId: effectiveProjectId, messages: [] },
          });
        } else {
          const effectiveWorkspaceId =
            selectedWorkspaceId || user?.currentWorkspaceId || undefined;
          const chatResponse = await chatsApi.create({
            ...(effectiveWorkspaceId ? { workspaceId: effectiveWorkspaceId } : {}),
            title,
          });
          const newChat = convertChatResponse(chatResponse);
          chatId = newChat.id;
          dispatch({ type: 'ADD_STANDALONE_CHAT', payload: newChat });
          dispatch({
            type: 'SELECT_CHAT',
            payload: { chatId, projectId: undefined, messages: [] },
          });
          trackStartChat();
        }
      }

      const hasAttachments = !!(options?.files?.length || options?.uiAttachments?.length);
      trackSendMessage(hasAttachments);

      // Add user message locally
      addMessage({
        role: 'user',
        content,
        ...(options?.uiAttachments?.length ? { attachments: options.uiAttachments } : {}),
      });

      // Add assistant placeholder message (shows "thinking" animation while waiting)
      addMessage({
        role: 'assistant',
        content: '',
        loadingLabel: 'Raciocinando',
      });

      // Track tool call IDs so we can finalize their states when the stream ends.
      // Declared outside `try` so the `finally` block can access them.
      const pendingToolCallIds: string[] = [];
      const seenToolCallIds = new Set<string>();

      // Send message to API (credits consumed on backend; agent from chat/backend default)
      try {
        const payload = {
          message: content,
          ...(options?.dependencies ? { dependencies: options.dependencies } : {}),
          ...(options?.metadata ? { metadata: options.metadata } : {}),
          ...(options?.outputSchema ? { outputSchema: options.outputSchema } : {}),
        };

        setIsStreaming(true);

        const stream = streamMessage(chatId, payload);
        let accumulatedContent = '';

        // Accumulate reasoning data across multiple events
        let accumulatedReasoningContent = '';
        const accumulatedReasoningSteps: any[] = [];
        const accumulatedToolUsage: any[] = [];
        let accumulatedPreviewHtml: string | null = null;
        const accumulatedIdeFiles: Array<{ path: string; language: string; content: string }> = [];
        const accumulatedPlanSteps: Array<{ label: string; description?: string; status: string }> = [];

        const languageForPath = (path: string): string => {
          const p = String(path || '').toLowerCase();
          if (p.endsWith('.ts')) return 'typescript';
          if (p.endsWith('.tsx')) return 'tsx';
          if (p.endsWith('.js')) return 'javascript';
          if (p.endsWith('.jsx')) return 'jsx';
          if (p.endsWith('.json')) return 'json';
          if (p.endsWith('.css')) return 'css';
          if (p.endsWith('.scss')) return 'scss';
          if (p.endsWith('.md')) return 'markdown';
          if (p.endsWith('.html')) return 'html';
          if (p.endsWith('.yml') || p.endsWith('.yaml')) return 'yaml';
          if (p.endsWith('.sh')) return 'bash';
          if (p.endsWith('.diff') || p.endsWith('.patch')) return 'diff';
          return 'text';
        };

        const buildWorkflowFromAccumulated = (params: { stage?: string }) => {
          const stage =
            params.stage ||
            (accumulatedPreviewHtml
              ? 'preview'
              : accumulatedIdeFiles.length > 0
                ? 'ide'
                : accumulatedToolUsage.length > 0
                  ? 'tasks'
                  : 'reasoning');
          return {
            stage,
            reasoning: {
              title: 'Reasoning',
              steps: accumulatedReasoningSteps.map((s: any) => ({
                label: String(s?.title || s?.label || 'Passo'),
                description: s?.content ? String(s.content) : (s?.description ? String(s.description) : undefined),
                status:
                  s?.status === 'completed' ? 'complete' : s?.status === 'in_progress' ? 'active' : 'pending',
              })),
            },
            tasks: {
              title: 'Tasks',
              items: accumulatedToolUsage.map((t: any) => ({
                title: String(t?.toolName || 'Ferramenta'),
                status:
                  t?.state === 'result'
                    ? 'completed'
                    : t?.state === 'output-error' || t?.state === 'rejected'
                      ? 'error'
                      : t?.state === 'call' || t?.state === 'approved'
                        ? 'in_progress'
                        : 'pending',
              })),
            },
            plan: {
              title: 'Plano',
              description: 'Plano derivado do workflow do agent-core.',
              steps: accumulatedPlanSteps.map((s) => ({
                label: s.label,
                description: s.description,
                status:
                  s.status === 'completed'
                    ? 'complete'
                    : s.status === 'in_progress'
                      ? 'active'
                      : 'pending',
              })),
            },
            planApproved: true,
            ide: {
              files: accumulatedIdeFiles,
              activePath: null,
              openPaths: [],
              terminalCollapsed: false,
              terminal: [],
            },
            preview: { html: accumulatedPreviewHtml },
          };
        };

        const upsertToolUsage = (tool: any) => {
          const id = tool?.toolCallId;
          if (!id) return;
          const idx = accumulatedToolUsage.findIndex((t) => t?.toolCallId === id);
          if (idx === -1) {
            accumulatedToolUsage.push(tool);
            return;
          }
          accumulatedToolUsage[idx] = { ...accumulatedToolUsage[idx], ...tool };
        };

        for await (const { event, data } of stream) {
          if (event === 'message') {
            accumulatedContent += data.content;
            updateLastMessage(accumulatedContent);
          } else if (event === 'reasoning') {
            // Chain-of-thought / reasoning events
            if (data.reasoning_content) {
              accumulatedReasoningContent += data.reasoning_content;
            }
            if (data.reasoning_steps && Array.isArray(data.reasoning_steps)) {
              accumulatedReasoningSteps.length = 0;
              accumulatedReasoningSteps.push(...data.reasoning_steps);
            }
            // Update message metadata with accumulated reasoning
            updateMessageMetadata({
              ...(accumulatedReasoningContent ? { reasoning_content: accumulatedReasoningContent } : {}),
              ...(accumulatedReasoningSteps.length > 0 ? { reasoning_steps: accumulatedReasoningSteps } : {}),
              workflow: buildWorkflowFromAccumulated({ stage: 'reasoning' }),
            });
          } else if (event === 'tool_call') {
            const toolCallId = typeof data?.toolCallId === 'string' ? data.toolCallId : null;
            if (toolCallId) {
              upsertToolUsage({
                toolCallId,
                toolName: data.toolName,
                args: data.args,
                state: data.state || 'call',
              });
              addToolUsage({
                toolCallId,
                toolName: data.toolName,
                args: data.args,
                state: data.state || 'call',
              });
              updateMessageMetadata({ workflow: buildWorkflowFromAccumulated({}) });
              seenToolCallIds.add(toolCallId);
              pendingToolCallIds.push(toolCallId);
            }
          } else if (event === 'tool_approval') {
            const toolCallId = typeof data?.toolCallId === 'string' ? data.toolCallId : null;
            if (toolCallId) {
              const rawState = typeof data?.state === 'string' ? data.state : 'approval-requested';
              const approvalMeta =
                rawState === 'approved' || rawState === 'rejected'
                  ? {
                      approvedBy: typeof data?.approvedBy === 'string' ? data.approvedBy : undefined,
                      approvedAt: typeof data?.approvedAt === 'string' ? data.approvedAt : undefined,
                    }
                  : undefined;
              const nextState =
                rawState === 'approved'
                  ? 'approved'
                  : rawState === 'rejected'
                    ? 'rejected'
                    : 'pending_approval';

              if (!seenToolCallIds.has(toolCallId)) {
                upsertToolUsage({
                  toolCallId,
                  toolName: data.toolName,
                  args: data.args,
                  state: nextState,
                  ...(approvalMeta ? approvalMeta : {}),
                });
                addToolUsage({
                  toolCallId,
                  toolName: data.toolName,
                  args: data.args,
                  state: nextState,
                  ...(approvalMeta ? approvalMeta : {}),
                });
                seenToolCallIds.add(toolCallId);
              } else {
                upsertToolUsage({
                  toolCallId,
                  state: nextState,
                  ...(approvalMeta ? approvalMeta : {}),
                });
                updateToolState(toolCallId, nextState, undefined, approvalMeta);
              }
              updateMessageMetadata({ workflow: buildWorkflowFromAccumulated({}) });
              pendingToolCallIds.push(toolCallId);
            }
          } else if (event === 'tool_result') {
            // Tool completed with result or error (state: 'result' | 'output-error')
            const toolCallId = typeof data?.toolCallId === 'string' ? data.toolCallId : null;
            const nextState = data.state === 'output-error' ? 'output-error' : 'result';
            if (toolCallId) {
              if (!seenToolCallIds.has(toolCallId)) {
                upsertToolUsage({
                  toolCallId,
                  toolName: data.toolName,
                  args: data.args,
                  state: nextState,
                  result: data.result,
                });
                addToolUsage({
                  toolCallId,
                  toolName: data.toolName,
                  args: data.args,
                  state: nextState,
                  result: data.result,
                });
                seenToolCallIds.add(toolCallId);
              } else {
                upsertToolUsage({
                  toolCallId,
                  state: nextState,
                  result: data.result,
                });
                updateToolState(toolCallId, nextState, data.result);
              }
            }
            if (data?.toolName === 'code_generator' && data?.result?.output) {
              const output = data.result.output;
              const previewHtml =
                typeof output?.preview?.html === 'string' ? output.preview.html : null;
              if (previewHtml) {
                accumulatedPreviewHtml = previewHtml;
              }
              accumulatedPlanSteps.length = 0;
              if (Array.isArray(output?.ops) && output.ops.length > 0) {
                for (const op of output.ops) {
                  if (!op || typeof op !== 'object') continue;
                  if (op.op === 'upsert' && typeof op.path === 'string') {
                    accumulatedPlanSteps.push({
                      label: `Criar/atualizar: ${op.path}`,
                      description: 'Gerar conteúdo do arquivo',
                      status: 'completed',
                    });
                  } else if (op.op === 'patch' && typeof op.path === 'string') {
                    accumulatedPlanSteps.push({
                      label: `Aplicar patch: ${op.path}`,
                      description: 'Atualizar trecho do arquivo',
                      status: 'completed',
                    });
                  } else if (op.op === 'delete' && typeof op.path === 'string') {
                    accumulatedPlanSteps.push({
                      label: `Remover: ${op.path}`,
                      status: 'completed',
                    });
                  }
                }
              }
              if (Array.isArray(output?.commands) && output.commands.length > 0) {
                for (const cmd of output.commands) {
                  if (typeof cmd !== 'string' || !cmd.trim()) continue;
                  accumulatedPlanSteps.push({
                    label: `Executar: ${cmd}`,
                    status: 'pending',
                  });
                }
              }
              if (Array.isArray(output?.ops)) {
                const byPath = new Map<string, { path: string; language: string; content: string }>(
                  accumulatedIdeFiles.map((f) => [f.path, f]),
                );
                for (const op of output.ops) {
                  if (!op || typeof op !== 'object') continue;
                  if (op.op === 'upsert' && typeof op.path === 'string' && typeof op.content === 'string') {
                    byPath.set(op.path, {
                      path: op.path,
                      language: languageForPath(op.path),
                      content: op.content,
                    });
                  } else if (op.op === 'patch' && typeof op.path === 'string' && typeof op.patch === 'string') {
                    byPath.set(op.path, {
                      path: op.path,
                      language: 'diff',
                      content: op.patch,
                    });
                  }
                }
                accumulatedIdeFiles.length = 0;
                accumulatedIdeFiles.push(...Array.from(byPath.values()));
              }
              if (!accumulatedPreviewHtml) {
                const htmlFile = accumulatedIdeFiles.find((f) => f.path.toLowerCase().endsWith('.html'));
                if (htmlFile?.content) {
                  accumulatedPreviewHtml = htmlFile.content;
                }
              }
              updateMessageMetadata({
                workflow: buildWorkflowFromAccumulated({
                  stage: accumulatedPreviewHtml ? 'preview' : accumulatedIdeFiles.length > 0 ? 'ide' : 'tasks',
                }),
              });
              if (accumulatedPreviewHtml) {
                setSidePanelTab('preview');
                setSidePanelOpen(true);
              } else if (accumulatedIdeFiles.length > 0) {
                setSidePanelTab('ide');
                setSidePanelOpen(true);
              }
            } else {
              updateMessageMetadata({ workflow: buildWorkflowFromAccumulated({}) });
            }
            const maybePreviewUrl = extractPreviewUrlFromToolResult(data?.result);
            if (maybePreviewUrl) {
              setPreviewUrl(maybePreviewUrl);
              setSidePanelTab('preview');
              setSidePanelOpen(true);
            }
          } else if (event === 'suggestions') {
            // Suggestions for next user actions
            if (data.suggestions && Array.isArray(data.suggestions)) {
              updateMessageMetadata({ suggestions: data.suggestions });
            }
          } else if (event === 'content_replace') {
            // Replace accumulated content with clean version (stripped of [SUGGESTIONS])
            if (typeof data.content === 'string') {
              accumulatedContent = data.content;
              updateLastMessage(accumulatedContent);
            }
          } else if (event === 'done') {
            // Stream finished: mark any tools still in 'call' state as 'result'
            for (const id of pendingToolCallIds) {
              updateToolState(id, 'result');
            }
            updateMessageMetadata({ workflow: buildWorkflowFromAccumulated({ stage: 'done' }) });
          } else if (event === 'error') {
            const rawError = typeof data?.error === 'string' ? data.error : 'Erro desconhecido';
            console.error('Stream error:', rawError);
            // Show user-friendly message for quota/limit errors
            const friendlyError =
              /402|spend limit|spending limit|quota|insufficient_quota|rate limit|429/i.test(rawError)
                ? 'O limite de uso da API de IA foi atingido. Atualize o plano ou verifique o limite de gastos da sua chave de API.'
                : rawError;
            updateLastMessage(friendlyError);
          }
        }

      } catch (error: unknown) {
        // If error is about missing agent, show specific message
        const maybeMessage =
          typeof error === 'object' && error && 'message' in error
            ? String((error as { message?: unknown }).message)
            : '';
        if (maybeMessage.includes('agent')) {
          updateLastMessage(
            'Por favor, selecione um projeto antes de enviar mensagens.',
          );
        } else {
          console.error('Error sending message:', error);
          updateLastMessage('Desculpe, ocorreu um erro ao processar sua mensagem.');
        }
      } finally {
        // Failsafe: finalize any tools still in 'call' state even if 'done'
        // event was never received (e.g. connection dropped).
        for (const id of pendingToolCallIds) {
          updateToolState(id, 'result');
        }
        setIsStreaming(false);
        // Sync credits from server (backend consumes credits when processing the message)
        refreshCredits().catch(() => {});
      }

      return chatId;
    } catch (error) {
      console.error('Error sending message:', error);
      // Best-effort: overwrite the placeholder assistant message if present
      updateLastMessage('Desculpe, ocorreu um erro ao processar sua mensagem.');

      // Keep the best-effort chat id (useful for route sync)
      return state.selectedChatId || '';
    }
  }, [
    state.selectedProjectId,
    state.selectedChatId,
    state.projects,
    selectedWorkspaceId,
    user?.currentWorkspaceId,
    createChat,
    addMessage,
    updateLastMessage,
    updateMessageMetadata,
    addToolUsage,
    updateToolState,
    setIsStreaming,
    refreshCredits,
  ]);


  const setMessages = useCallback((messages: Message[]) => {
    dispatch({ type: 'SET_MESSAGES', payload: messages });
  }, []);

  const deleteMessageById = useCallback(async (messageId: string) => {
    const chatId = state.selectedChatId;
    if (!chatId) return;

    try {
      await apiDeleteMessage(chatId, messageId);
      dispatch({ type: 'DELETE_MESSAGE', payload: { messageId } });
    } catch (error) {
      console.error('Error deleting message:', error);
      throw error;
    }
  }, [state.selectedChatId]);

  const regenerateMessage = useCallback(async (messageId: string) => {
    const chatId = state.selectedChatId;
    if (!chatId) return;

    // Find the assistant message to regenerate
    const msgIndex = state.messages.findIndex((m) => m.id === messageId);
    if (msgIndex === -1) return;

    const assistantMsg = state.messages[msgIndex];
    if (assistantMsg.role !== 'assistant') return;

    // Find the preceding user message
    let userMessage: Message | undefined;
    for (let i = msgIndex - 1; i >= 0; i--) {
      if (state.messages[i].role === 'user') {
        userMessage = state.messages[i];
        break;
      }
    }
    if (!userMessage || !userMessage.content) return;

    // Track regeneration count
    const currentCount = assistantMsg.metadata?.regenerationCount || 0;

    try {
      // Delete the assistant message from the backend
      await apiDeleteMessage(chatId, messageId);
      // Remove from local state
      dispatch({ type: 'DELETE_MESSAGE', payload: { messageId } });

      // Also delete the user message from backend (sendMessage will re-create it)
      if (userMessage.id) {
        try {
          await apiDeleteMessage(chatId, userMessage.id);
          dispatch({ type: 'DELETE_MESSAGE', payload: { messageId: userMessage.id } });
        } catch {
          // User message might not exist in DB (e.g. client-only), ignore
        }
      }

      // Re-send the user message via stream (creates both user + assistant messages)
      await sendMessage(userMessage.content, {
        metadata: { regenerationCount: currentCount + 1 },
      });
    } catch (error) {
      console.error('Error regenerating message:', error);
      throw error;
    }
  }, [state.selectedChatId, state.messages, sendMessage]);

  const registerChatInput = useCallback((el: HTMLElement | null) => {
    chatInputRef.current = el;
  }, []);

  // Actions - Refs
  const focusChatInput = useCallback(() => {
    chatInputRef.current?.focus();
  }, []);

  // Helpers
  const getCurrentProject = useCallback(() => {
    return state.projects.find((p) => p.id === state.selectedProjectId);
  }, [state.projects, state.selectedProjectId]);

  const getCurrentChat = useCallback(() => {
    return state.standaloneChats.find((c) => c.id === state.selectedChatId) || state.projects.find((p) => p.id === state.selectedProjectId)?.chats.find((c) => c.id === state.selectedChatId);
  }, [state.standaloneChats, state.projects, state.selectedChatId, state.selectedProjectId]);

  const getCurrentAgent = useCallback((): Agent => {
    return DEFAULT_AGENT_DISPLAY;
  }, []);

  const value: ChatContextValue = {
    ...state,
    workspaceId: selectedWorkspaceId,
    sidePanelOpen,
    sidePanelTab,
    previewUrl,
    openSidePanel,
    closeSidePanel,
    setPreviewUrl,
    chatMode,
    setChatMode,
    addProject,
    setSelectedProject,
    loadProjects,
    createChat,
    selectChat,
    clearChat,
    addMessage,
    sendMessage,
    updateLastMessage,
    updateMessageMetadata,
    addToolUsage,
    updateToolState,
    deleteMessageById,
    regenerateMessage,
    setMessages,
    setIsStreaming,
    startTypingAnimation,
    stopTypingAnimation,
    registerChatInput,
    focusChatInput,
    setSuggestedMessage,
    suggestedMessage,
    getCurrentProject,
    getCurrentChat,
    getCurrentAgent,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

// Hook
export function useChatContext() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
}
